import {defineStore} from 'pinia'
import {CommonNs, ProductNs, ProjectNs, WorkspaceNs} from "@shared/types";
import {AiApi, SystemApi, WorkspaceApi} from "@/api";
import {websocket} from "@shared/helpers";
import type {Subscription} from "stompjs";


export const useWorkspaceStore = defineStore("workspace", {
  state: () => ({
    selectedWorkspace: {} as WorkspaceNs.Workspace,
    workspaceList: [] as WorkspaceNs.Workspace[],
    onlineUserList: [] as string[],
    onlineUserSubscription: undefined as Subscription | undefined,
    aiBalance: -1
  }),
  getters: {
    getWorkspaceList: (state): WorkspaceNs.Workspace[] => {
      state.workspaceList.forEach((workspace: WorkspaceNs.Workspace) => {
        workspace.isActive = workspace.id === (state.selectedWorkspace?.id || -1);
      });

      return state.workspaceList;
    },

    getSelectedWorkspace: (state): WorkspaceNs.Workspace => state.selectedWorkspace!,

    getSelectedWorkspaceId: (state): string => state.selectedWorkspace?.id || "-1",

    getWorkspaceRole: (state): "OWNER" | "ADMIN" | "MEMBER" => state.selectedWorkspace?.userRole || 'MEMBER',

    /** -1 until the first answer arrives. */
    getAIBalance: (state): number => state.aiBalance,

    getProduct: (state): ProductNs.ProductType => {
      return state.selectedWorkspace?.productConfig?.productKey || ProductNs.ProductType.FREE;
    },

    hasFreeProduct: (state): boolean => {
      return !state.selectedWorkspace?.productConfig?.productKey ||
          state.selectedWorkspace.productConfig.productKey === ProductNs.ProductType.FREE;
    },

    hasProProduct: (state): boolean => {
      return state.selectedWorkspace && state.selectedWorkspace.productConfig
          && state.selectedWorkspace.productConfig.productKey &&
          state.selectedWorkspace.productConfig.productKey === ProductNs.ProductType.PROFESSIONAL;
    },

    hasBusinessProduct: (state): boolean => {
      return state.selectedWorkspace && state.selectedWorkspace.productConfig
          && state.selectedWorkspace.productConfig.productKey &&
          state.selectedWorkspace.productConfig.productKey === ProductNs.ProductType.BUSINESS;
    }
  },
  actions: {
    switchToWorkspace(selectedWorkspace: WorkspaceNs.Workspace): void {
      websocket.disconnect();
      this.selectedWorkspace = selectedWorkspace;
      websocket.connect(selectedWorkspace.id)

      setTimeout(() => {
        SystemApi.getOnlineUser(selectedWorkspace.id)
        .then((userList: string[]) => {
          this.onlineUserList = userList
        })
      }, 2000)
    },

    async loadWorkspace(workspaceId: string): Promise<WorkspaceNs.Workspace> {
      return WorkspaceApi.getWorkspace(workspaceId)
      .then((workspace) => {
        this.selectedWorkspace = workspace
        return workspace;
      })
    },

    async loadUserWorkspaceList(): Promise<WorkspaceNs.Workspace[]> {
      return WorkspaceApi.getWorkspaceList()
      .then((workspaceList) => {
        this.workspaceList = workspaceList;
        return workspaceList;
      })
    },

    async sendDeleteWorkspaceMail(payload: WorkspaceNs.WorkspaceActionRequest): Promise<CommonNs.AnyResponse> {
      return WorkspaceApi.sendDeleteWorkspaceMail(payload)
      .then((response) => response)
    },

    async confirmDeleteWorkspace(payload: WorkspaceNs.ConfirmWorkspaceActionRequest): Promise<CommonNs.AnyResponse> {
      return WorkspaceApi.confirmDeleteWorkspace(payload)
      .then((response) => response)
    },

    async leftWorkspace(payload: WorkspaceNs.WorkspaceActionRequest): Promise<CommonNs.AnyResponse> {
      return WorkspaceApi.leftWorkspace(payload);
    },

    async sendUpgradeRequest(payload: ProjectNs.GenericProjectRequest): Promise<CommonNs.AnyResponse> {
      return WorkspaceApi.sendUpgradeRequest(payload).then((response: CommonNs.AnyResponse) => response);
    },

    async loadAIBalance(): Promise<number> {
      const wsStore = useWorkspaceStore();
      const workspaceId = wsStore.getSelectedWorkspaceId;

      return AiApi.getAIBalance(workspaceId).then((balance: number) => {
        this.aiBalance = balance;
        return balance;
      });
    },

    /** What the `/user/queue/requestLimit` message carries, after a turn is spent. */
    setAIBalance(balance: number): void {
      this.aiBalance = balance;
    }
  }
});
